import React, { useState, useEffect, useRef } from 'react';
import CalmiCharacter from '../pet/components/CalmiCharacter';
import { PLAY_ACTIVITIES, DIARY_PROMPTS } from '../../shared/constants';
import { useGender, Gender } from '../../shared/hooks/useGender';
import { Card, NavigationBar, Button, ThemedScreen } from '../../shared/components';
import DrawingCanvas from '../chat/DrawingCanvas';
import { storageService } from '../../shared/services';

const SUPERPOWERS_MASCULINE = [
  { 
    icon: '🦁', 
    title: 'Valentía', 
    text: 'Soy valiente y enfrento mis miedos',
    fullText: 'El coraje no significa no tener miedo, sino decidir que hay cosas más importantes que el miedo. Cada vez que enfrento algo que me asusta, mi valentía crece un poquito más. Hoy elijo ser valiente, respirar hondo y dar un paso adelante, porque sé que puedo con esto y con mucho más.'
  },
  { 
    icon: '⚡', 
    title: 'Energía', 
    text: 'Puedo hacer cosas increíbles',
    fullText: 'Dentro de mí hay una energía enorme, como un rayo de luz lista para brillar. Puedo aprender, crear, jugar, ayudar y sorprenderme a mí mismo cada día. Cuando confío en mis habilidades, descubro que soy capaz de lograr cosas que antes parecían imposibles.'
  },
  { 
    icon: '✨', 
    title: 'Especial', 
    text: 'Soy especial y único',
    fullText: 'No hay nadie en el mundo exactamente igual a mí. Mis sueños, mi risa, mi forma de pensar y mi corazón son únicos. Eso me hace especial y valioso exactamente como soy. No necesito ser como los demás, porque el mundo necesita lo que solo yo puedo aportar.'
  },
  { 
    icon: '❤️', 
    title: 'Amistad', 
    text: 'Tengo amigos que me quieren',
    fullText: 'A mi alrededor hay personas que me quieren y se preocupan por mí. La amistad es un superpoder que me hace sentir acompañado, seguro y feliz. Cuando comparto, escucho y cuido a los demás, también estoy cuidando mi propio corazón.'
  },
  { 
    icon: '🎨', 
    title: 'Creatividad', 
    text: 'Soy creativo e imaginativo',
    fullText: 'Mi imaginación es un mundo sin límites. Puedo inventar historias, dibujar universos, encontrar soluciones nuevas y soñar en grande. La creatividad me permite transformar cualquier momento en algo mágico y especial, porque mis ideas importan.'
  },
  { 
    icon: '🌟', 
    title: 'Autoestima', 
    text: 'Me caigo bien tal como soy',
    fullText: 'Me acepto y me quiero tal como soy, con mis cosas buenas y también con las que todavía estoy aprendiendo. No necesito ser perfecto para ser valioso. Cada día me esfuerzo, y eso ya es suficiente. Me caigo bien, y eso es un superpoder muy poderoso.'
  }
];

const SUPERPOWERS_FEMININE = [
  { 
    icon: '🦁', 
    title: 'Valentía', 
    text: 'Soy valiente y enfrento mis miedos',
    fullText: 'El coraje no significa no tener miedo, sino decidir que hay cosas más importantes que el miedo. Cada vez que enfrento algo que me asusta, mi valentía crece un poquito más. Hoy elijo ser valiente, respirar hondo y dar un paso adelante, porque sé que puedo con esto y con mucho más.'
  },
  { 
    icon: '⚡', 
    title: 'Energía', 
    text: 'Puedo hacer cosas increíbles',
    fullText: 'Dentro de mí hay una energía enorme, como un rayo de luz lista para brillar. Puedo aprender, crear, jugar, ayudar y sorprenderme a mí misma cada día. Cuando confío en mis habilidades, descubro que soy capaz de lograr cosas que antes parecían imposibles.'
  },
  { 
    icon: '✨', 
    title: 'Especial', 
    text: 'Soy especial y única',
    fullText: 'No hay nadie en el mundo exactamente igual a mí. Mis sueños, mi risa, mi forma de pensar y mi corazón son únicos. Eso me hace especial y valiosa exactamente como soy. No necesito ser como los demás, porque el mundo necesita lo que solo yo puedo aportar.'
  },
  { 
    icon: '❤️', 
    title: 'Amistad', 
    text: 'Tengo amigas que me quieren',
    fullText: 'A mi alrededor hay personas que me quieren y se preocupan por mí. La amistad es un superpoder que me hace sentir acompañada, segura y feliz. Cuando comparto, escucho y cuido a los demás, también estoy cuidando mi propio corazón.'
  },
  { 
    icon: '🎨', 
    title: 'Creatividad', 
    text: 'Soy creativa e imaginativa',
    fullText: 'Mi imaginación es un mundo sin límites. Puedo inventar historias, dibujar universos, encontrar soluciones nuevas y soñar en grande. La creatividad me permite transformar cualquier momento en algo mágico y especial, porque mis ideas importan.'
  },
  { 
    icon: '🌟', 
    title: 'Autoestima', 
    text: 'Me caigo bien tal como soy',
    fullText: 'Me acepto y me quiero tal como soy, con mis cosas buenas y también con las que todavía estoy aprendiendo. No necesito ser perfecta para ser valiosa. Cada día me esfuerzo, y eso ya es suficiente. Me caigo bien, y eso es un superpoder muy poderoso.'
  }
];

const getSuperpowers = (gender: Gender | null) => {
  if (gender === 'female') return SUPERPOWERS_FEMININE;
  return SUPERPOWERS_MASCULINE;
};

const PlayScreen: React.FC<{ userName: string; onNavigate: (screen: string) => void }> = ({ userName, onNavigate }) => {
  const { gender } = useGender();
  const superpowers = getSuperpowers(gender);
  const totalPowers = superpowers.length;
  const [selectedActivity, setSelectedActivity] = useState<string | null>(null);
  const [bubbles, setBubbles] = useState<Array<{ id: number; x: number; y: number }>>([]);
  const [bubbleCount, setBubbleCount] = useState(0);
  const [currentAffirmation, setCurrentAffirmation] = useState(0);
  const [powerUsage, setPowerUsage] = useState<Record<number, number>>(() => {
    const saved = localStorage.getItem('calmi_superpowers_usage');
    return saved ? JSON.parse(saved) : {};
  });
  const [showPowerMessage, setShowPowerMessage] = useState(false);
  const [storyInput, setStoryInput] = useState('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTrack, setCurrentTrack] = useState(0);
  const [selectedDrawingEmotion, setSelectedDrawingEmotion] = useState<string | null>(null);
  const [showDrawingCanvas, setShowDrawingCanvas] = useState(false);
  
  // Bubble game state
  const [gameActive, setGameActive] = useState(false);
  const [gameScore, setGameScore] = useState(0);
  const [gameLevel, setGameLevel] = useState(1);
  const [maxBubbles, setMaxBubbles] = useState(8);
  const [spawnRate, setSpawnRate] = useState(1500);
  const [gameWon, setGameWon] = useState(false);
  const [gameLost, setGameLost] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60);
  const [combo, setCombo] = useState(0);
  const [specialBubbles, setSpecialBubbles] = useState<Array<{ id: number; x: number; y: number; type: 'golden' | 'bomb' }>>([]);

  const handleBubbleClick = () => {
    const newBubble = {
      id: Date.now(),
      x: Math.random() * 80 + 10,
      y: Math.random() * 60 + 20
    };
    setBubbles([...bubbles, newBubble]);
    setBubbleCount(bubbleCount + 1);
  };

  const handleBubblePop = (id: number) => {
    setBubbles(bubbles.filter(b => b.id !== id));
  };

  const handleDrawingEmotionSelect = (emotion: string) => {
    setSelectedDrawingEmotion(emotion);
    setShowDrawingCanvas(true);
  };

  const handleDrawingSave = (imageData: string) => {
    if (selectedDrawingEmotion) {
      storageService.saveDrawing(selectedDrawingEmotion, imageData);
      storageService.saveEmotion(selectedDrawingEmotion, 'Dibujé mis sentimientos');
    }
    setShowDrawingCanvas(false);
    setSelectedDrawingEmotion(null);
  };

  const handleDrawingCancel = () => {
    setShowDrawingCanvas(false);
    setSelectedDrawingEmotion(null);
  };

  // Bubble game logic
  const gameIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startBubbleGame = () => {
    setGameActive(true);
    setGameScore(0);
    setGameLevel(1);
    setMaxBubbles(8);
    setSpawnRate(1500);
    setBubbles([]);
    setGameWon(false);
    setGameLost(false);
    setTimeLeft(60);
    setCombo(0);
    setSpecialBubbles([]);
  };

  const stopBubbleGame = () => {
    setGameActive(false);
    if (gameIntervalRef.current) {
      clearInterval(gameIntervalRef.current);
      gameIntervalRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (gameActive) {
      gameIntervalRef.current = setInterval(() => {
        setBubbles(prev => {
          if (prev.length >= maxBubbles) {
            setGameLost(true);
            stopBubbleGame();
            return prev;
          }
          const newBubble = {
            id: Date.now(),
            x: Math.random() * 85 + 5,
            y: Math.random() * 80 + 10
          };
          return [...prev, newBubble];
        });

        // Spawn special bubbles occasionally
        if (Math.random() < 0.15) {
          const type = Math.random() < 0.6 ? 'golden' : 'bomb';
          setSpecialBubbles(prev => [...prev, {
            id: Date.now() + 1,
            x: Math.random() * 85 + 5,
            y: Math.random() * 80 + 10,
            type
          }]);
        }
      }, spawnRate);

      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            setGameLost(true);
            stopBubbleGame();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (gameIntervalRef.current) {
        clearInterval(gameIntervalRef.current);
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [gameActive, spawnRate, maxBubbles]);

  const handleGameBubblePop = (id: number) => {
    if (!gameActive) return;
    
    setBubbles(prev => {
      const newBubbles = prev.filter(b => b.id !== id);
      setGameScore(prevScore => {
        const newScore = prevScore + 1;
        setCombo(prevCombo => prevCombo + 1);
        
        // Level up every 8 bubbles (harder)
        if (newScore % 8 === 0) {
          setGameLevel(prevLevel => prevLevel + 1);
          setMaxBubbles(prev => Math.min(prev + 3, 15)); // Cap at 15
          setSpawnRate(prev => Math.max(prev - 150, 400)); // Minimum 400ms
        }
        
        // Win condition: pop 60 bubbles (harder)
        if (newScore >= 60) {
          setGameWon(true);
          stopBubbleGame();
        }
        
        return newScore;
      });
      
      return newBubbles;
    });
  };

  const handleSpecialBubblePop = (id: number, type: 'golden' | 'bomb') => {
    if (!gameActive) return;
    
    setSpecialBubbles(prev => prev.filter(b => b.id !== id));
    
    if (type === 'golden') {
      setGameScore(prev => prev + 5);
      setCombo(prevCombo => prevCombo + 5);
      setTimeLeft(prev => prev + 5); // Bonus time
    } else if (type === 'bomb') {
      setGameScore(prev => Math.max(0, prev - 3));
      setCombo(0);
    }
  };

  const nextAffirmation = () => {
    setCurrentAffirmation((currentAffirmation + 1) % totalPowers);
  };

  const handlePowerSelect = (index: number) => {
    setCurrentAffirmation(index);
    const newUsage = { ...powerUsage, [index]: (powerUsage[index] || 0) + 1 };
    setPowerUsage(newUsage);
    localStorage.setItem('calmi_superpowers_usage', JSON.stringify(newUsage));
    setShowPowerMessage(true);
    setTimeout(() => setShowPowerMessage(false), 5000);
  };

  const totalPowerUses = Object.keys(powerUsage).reduce((sum, key) => sum + (powerUsage[parseInt(key)] || 0), 0);

  const lofiTracks = [
    { name: 'Lofi Chill 1', url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3' },
    { name: 'Lofi Chill 2', url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-2.mp3' },
    { name: 'Lofi Chill 3', url: 'https://www.soundhelix.com/examples/mp3/SoundHelix-Song-3.mp3' }
  ];

  const togglePlay = () => {
    setIsPlaying(!isPlaying);
  };

  const nextTrack = () => {
    setCurrentTrack((currentTrack + 1) % lofiTracks.length);
  };

  const prevTrack = () => {
    setCurrentTrack((currentTrack - 1 + lofiTracks.length) % lofiTracks.length);
  };

  const renderActivity = () => {
    switch (selectedActivity) {
      case 'bubbles':
        if (gameWon) {
          return (
            <div className="text-center">
              <h2 className="text-3xl sm:text-4xl font-semibold text-slate-800 mb-4">
                ¡Felicidades! 🎉
              </h2>
              <p className="text-lg sm:text-xl text-slate-700 mb-4 font-medium">¡Completaste el desafío de burbujas!</p>
              <div className="text-7xl mb-6 animate-subtle-float">🏆</div>
              <Card className="mb-6 p-6 bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 shadow-md">
                <p className="text-2xl font-semibold text-slate-800 mb-2">⭐ Puntuación: {gameScore}</p>
                <p className="text-xl font-semibold text-slate-700 mb-2">🎯 Nivel alcanzado: {gameLevel}</p>
                <p className="text-lg font-medium text-slate-600">¡Eres un campeón afrontando retos!</p>
              </Card>
              <div className="flex gap-3 justify-center">
                <Button onClick={startBubbleGame} className="btn-interactive bg-gradient-to-r from-amber-400 to-orange-400 hover:from-amber-500 hover:to-orange-500 text-white font-semibold py-3 px-8 text-lg rounded-full shadow-md hover:shadow-lg transition-all">
                  🔄 ¡Jugar de nuevo!
                </Button>
                <Button onClick={() => { setGameWon(false); setSelectedActivity(null); }} variant="secondary" className="py-3 px-6 btn-interactive">
                  ← Volver
                </Button>
              </div>
            </div>
          );
        }

        if (gameLost) {
          return (
            <div className="text-center">
              <h2 className="text-3xl sm:text-4xl font-semibold text-slate-800 mb-4">
                ¡Casi lo logras! 💪
              </h2>
              <p className="text-lg sm:text-xl text-slate-700 mb-4 font-medium">
                {timeLeft === 0 ? '¡Se acabó el tiempo!' : '¡Hay demasiadas burbujas!'}
              </p>
              <div className="text-7xl mb-6 animate-subtle-float">😅</div>
              <Card className="mb-6 p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 shadow-md">
                <p className="text-2xl font-semibold text-slate-800 mb-2">⭐ Puntuación: {gameScore}</p>
                <p className="text-xl font-semibold text-slate-700 mb-2">🎯 Nivel alcanzado: {gameLevel}</p>
                <p className="text-lg font-medium text-slate-600">¡Inténtalo de nuevo, puedes hacerlo!</p>
              </Card>
              <div className="flex gap-3 justify-center">
                <Button onClick={startBubbleGame} className="btn-interactive bg-gradient-to-r from-blue-400 to-indigo-400 hover:from-blue-500 hover:to-indigo-500 text-white font-semibold py-3 px-8 text-lg rounded-full shadow-md hover:shadow-lg transition-all">
                  🔄 ¡Intentar de nuevo!
                </Button>
                <Button onClick={() => { setGameLost(false); setSelectedActivity(null); }} variant="secondary" className="py-3 px-6 btn-interactive">
                  ← Volver
                </Button>
              </div>
            </div>
          );
        }

        if (!gameActive) {
          return (
            <div className="text-center">
              <h2 className="text-2xl sm:text-3xl font-semibold text-slate-800 mb-4">
                ¡Desafío de Burbujas! 🫧
              </h2>
              <p className="text-sm sm:text-base text-slate-600 mb-6 font-medium">
                ¡Reventa 60 burbujas en 60 segundos! ¡Cuidado con las bombas! 💣
              </p>
              
              <Card className="mb-6 p-6 bg-white/90 backdrop-blur-sm border border-slate-200 shadow-md">
                <div className="text-6xl mb-4 animate-subtle-float">🎮</div>
                <p className="text-lg font-semibold text-slate-700 mb-4">Objetivo: 60 burbujas en 60 segundos</p>
                <div className="text-left text-sm text-slate-600 mb-4 space-y-2">
                  <p className="flex items-center gap-2"><span className="text-xl">⭐</span> Burbujas doradas: +5 puntos y +5 segundos</p>
                  <p className="flex items-center gap-2"><span className="text-xl">💣</span> Bombas: -3 puntos y rompe combo</p>
                  <p className="flex items-center gap-2"><span className="text-xl">🔥</span> ¡Haz combos para más puntos!</p>
                  <p className="flex items-center gap-2"><span className="text-xl">⚡</span> Cada nivel es más rápido</p>
                </div>
              </Card>
              
              <div className="flex gap-3 justify-center">
                <Button onClick={startBubbleGame} className="btn-interactive bg-gradient-to-r from-blue-400 to-blue-500 hover:from-blue-500 hover:to-blue-600 text-white font-semibold py-3 px-8 text-lg rounded-full shadow-md hover:shadow-lg transition-all">
                  ▶️ ¡Comenzar!
                </Button>
                <Button onClick={() => setSelectedActivity(null)} variant="secondary" className="py-3 px-6">
                  ← Volver
                </Button>
              </div>
            </div>
          );
        }

        return (
          <div className="text-center">
            <h2 className="text-2xl sm:text-3xl font-semibold text-slate-800 mb-4">
              ¡Desafío de Burbujas! 🫧
            </h2>
            
            {/* Timer Bar */}
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <p className="text-lg font-semibold text-slate-700">⏱️ Tiempo</p>
                <p className={`text-2xl font-semibold ${timeLeft <= 10 ? 'text-red-500 animate-pulse-soft' : 'text-slate-600'}`}>{timeLeft}s</p>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-4 overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all duration-1000 ${timeLeft <= 10 ? 'bg-gradient-to-r from-red-400 to-orange-400' : 'bg-gradient-to-r from-blue-400 to-indigo-400'}`}
                  style={{ width: `${(timeLeft / 60) * 100}%` }}
                />
              </div>
            </div>

            {/* Combo Display */}
            {combo > 0 && (
              <div className="mb-4 animate-subtle-float">
                <p className="text-xl font-semibold text-slate-700">
                  🔥 ¡Combo x{combo}!
                </p>
              </div>
            )}
            
            <div className="relative h-96 bg-gradient-to-b from-sky-100 via-blue-100 to-indigo-100 rounded-3xl mb-4 overflow-hidden cursor-crosshair border-2 border-slate-300 shadow-lg">
              {/* Background decorations */}
              <div className="absolute inset-0 opacity-20">
                <div className="absolute top-4 left-4 text-3xl animate-subtle-float">☁️</div>
                <div className="absolute top-8 right-8 text-2xl animate-subtle-float" style={{animationDelay: '0.5s'}}>☁️</div>
                <div className="absolute bottom-8 left-8 text-2xl animate-subtle-float" style={{animationDelay: '1s'}}>☁️</div>
              </div>
              
              {bubbles.map((bubble) => (
                <div
                  key={bubble.id}
                  onClick={(e) => { e.stopPropagation(); handleGameBubblePop(bubble.id); }}
                  className="absolute w-14 h-14 bg-gradient-to-br from-cyan-200 via-blue-300 to-indigo-300 rounded-full cursor-pointer hover:scale-110 transition-all flex items-center justify-center text-2xl shadow-md animate-float border border-white/50"
                  style={{ 
                    left: `${bubble.x}%`, 
                    top: `${bubble.y}%`
                  }}
                >
                  💨
                </div>
              ))}

              {specialBubbles.map((bubble) => (
                <div
                  key={bubble.id}
                  onClick={(e) => { e.stopPropagation(); handleSpecialBubblePop(bubble.id, bubble.type); }}
                  className={`absolute w-16 h-16 rounded-full cursor-pointer hover:scale-115 transition-all flex items-center justify-center text-3xl shadow-lg ${
                    bubble.type === 'golden' 
                      ? 'bg-gradient-to-br from-yellow-200 via-amber-300 to-orange-300 border-2 border-yellow-400 animate-glow' 
                      : 'bg-gradient-to-br from-red-200 via-red-300 to-red-400 border-2 border-red-400 animate-shake'
                  }`}
                  style={{ 
                    left: `${bubble.x}%`, 
                    top: `${bubble.y}%`
                  }}
                >
                  {bubble.type === 'golden' ? '⭐' : '💣'}
                </div>
              ))}
            </div>
            
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-3 border border-slate-200 shadow-sm">
                <p className="text-3xl font-semibold text-slate-700">{gameScore}</p>
                <p className="text-xs font-semibold text-slate-600">Puntos</p>
              </div>
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-3 border border-slate-200 shadow-sm">
                <p className="text-3xl font-semibold text-slate-700">{bubbles.length}/{maxBubbles}</p>
                <p className="text-xs font-semibold text-slate-600">Burbujas</p>
              </div>
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-3 border border-slate-200 shadow-sm">
                <p className="text-3xl font-semibold text-slate-700">{gameLevel}</p>
                <p className="text-xs font-semibold text-slate-600">Nivel</p>
              </div>
            </div>

            <div className="flex gap-3 justify-center">
              <Button onClick={stopBubbleGame} variant="secondary" className="py-3 px-6 btn-interactive">
                ⏹️ Detener
              </Button>
              <Button onClick={() => { stopBubbleGame(); setSelectedActivity(null); }} variant="secondary" className="py-3 px-6 btn-interactive">
                ← Volver
              </Button>
            </div>
          </div>
        );

      case 'drawing':
        return (
          <div className="text-center">
            {showDrawingCanvas ? (
              <DrawingCanvas onSave={handleDrawingSave} onCancel={handleDrawingCancel} />
            ) : (
              <>
                <h2 className="text-2xl sm:text-3xl font-semibold text-slate-800 mb-4">
                  Dibuja lo que sientes 🎨
                </h2>
                <p className="text-sm sm:text-base text-slate-600 mb-6 font-medium">
                  Expresa tus emociones a través del arte
                </p>
                
                <Card className="mb-6 p-6 bg-white/90 backdrop-blur-sm border border-slate-200 shadow-md">
                  <div className="text-6xl mb-4 animate-subtle-float">🎨</div>
                  <p className="text-lg font-semibold text-slate-700 mb-4">¿Qué te gustaría dibujar hoy?</p>
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <button 
                      onClick={() => handleDrawingEmotionSelect('happy')}
                      className="p-3 bg-pink-50 hover:bg-pink-100 rounded-xl border border-pink-200 transition-all btn-interactive"
                    >
                      <span className="text-2xl">😊</span>
                      <p className="text-sm text-slate-700 mt-1">Algo feliz</p>
                    </button>
                    <button 
                      onClick={() => handleDrawingEmotionSelect('sad')}
                      className="p-3 bg-blue-50 hover:bg-blue-100 rounded-xl border border-blue-200 transition-all btn-interactive"
                    >
                      <span className="text-2xl">😢</span>
                      <p className="text-sm text-slate-700 mt-1">Algo triste</p>
                    </button>
                    <button 
                      onClick={() => handleDrawingEmotionSelect('calm')}
                      className="p-3 bg-green-50 hover:bg-green-100 rounded-xl border border-green-200 transition-all btn-interactive"
                    >
                      <span className="text-2xl">😌</span>
                      <p className="text-sm text-slate-700 mt-1">Algo tranquilo</p>
                    </button>
                    <button 
                      onClick={() => handleDrawingEmotionSelect('free')}
                      className="p-3 bg-purple-50 hover:bg-purple-100 rounded-xl border border-purple-200 transition-all btn-interactive"
                    >
                      <span className="text-2xl">🌈</span>
                      <p className="text-sm text-slate-700 mt-1">Lo que quieras</p>
                    </button>
                  </div>
                </Card>
                
                <Button onClick={() => setSelectedActivity(null)} variant="secondary" className="py-3 px-6 btn-interactive">
                  ← Volver
                </Button>
              </>
            )}
          </div>
        );

      case 'superpowers':
        return (
          <div className="text-center">
            <h2 className="text-2xl sm:text-3xl font-semibold text-slate-800 mb-2">
              Mis Superpoderes ✨
            </h2>
            <p className="text-sm sm:text-base text-slate-600 mb-6 font-medium">
              ¡Descubre y colecciona tus poderes especiales!
            </p>
            
            {/* Superpower Cards Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
              {superpowers.map((power, index) => (
                <Card
                  key={index}
                  className={`p-4 cursor-pointer transition-all transform hover:scale-105 ${
                    currentAffirmation === index 
                      ? 'bg-gradient-to-br from-amber-100 to-orange-100 border-2 border-amber-400 shadow-lg' 
                      : 'bg-white/90 backdrop-blur-sm border border-slate-200 hover:border-amber-300'
                  }`}
                  onClick={() => handlePowerSelect(index)}
                >
                  <div className="text-3xl mb-2 animate-subtle-float">
                    {power.icon}
                  </div>
                  <p className="text-xs font-bold text-slate-800 mb-1">{power.title}</p>
                  <p className="text-xs sm:text-sm text-slate-700 font-medium line-clamp-3">
                    {power.text}
                  </p>
                  <div className="mt-2 text-xs text-slate-500">
                    Usado {powerUsage[index] || 0} {powerUsage[index] === 1 ? 'vez' : 'veces'}
                  </div>
                  {currentAffirmation === index && (
                    <div className="mt-1 text-xs text-amber-600 font-semibold animate-pulse">
                      ¡Activo! ⭐
                    </div>
                  )}
                </Card>
              ))}
            </div>

            {/* Selected Superpower Display */}
            <Card className="mb-4 p-6 bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-300 shadow-xl">
              <div className="text-5xl mb-4 animate-bounce-in">
                {superpowers[currentAffirmation].icon}
              </div>
              <h3 className="text-lg font-bold text-slate-800 mb-2">{superpowers[currentAffirmation].title}</h3>
              <p className="text-base sm:text-lg text-slate-700 font-medium mb-4 leading-relaxed">
                {superpowers[currentAffirmation].fullText}
              </p>
              <div className="flex items-center justify-center gap-2 text-sm text-slate-600">
                <span className="text-amber-500">⭐</span>
                <span>Superpoder {currentAffirmation + 1} de {totalPowers} · Usado {powerUsage[currentAffirmation] || 0} veces</span>
              </div>
            </Card>

            {showPowerMessage && (
              <Card className="mb-4 p-4 bg-green-50 border-2 border-green-300 shadow-lg animate-bounce-in">
                <p className="text-base text-green-800 font-semibold">
                  🎉 ¡Activaste tu superpoder! Sigue practicando para hacerlo más fuerte.
                </p>
              </Card>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <Button 
                onClick={nextAffirmation} 
                className="btn-interactive bg-gradient-to-r from-amber-400 to-orange-400 hover:from-amber-500 hover:to-orange-500 text-white font-semibold py-3 px-8 text-lg rounded-full shadow-md hover:shadow-lg transition-all flex-1"
              >
                🎲 Nuevo Poder
              </Button>
              <Button 
                onClick={() => handlePowerSelect(Math.floor(Math.random() * totalPowers))}
                variant="secondary"
                className="btn-interactive py-3 px-6 flex-1"
              >
                🎯 Sorpréndeme
              </Button>
            </div>

            {/* Power Stats */}
            <Card className="mb-6 p-4 bg-white/90 backdrop-blur-sm border border-slate-200">
              <h4 className="text-sm font-semibold text-slate-800 mb-3">📊 Tu Progreso</h4>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-amber-600">{totalPowerUses}</div>
                  <div className="text-xs text-slate-600">Prácticas totales</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{totalPowers}</div>
                  <div className="text-xs text-slate-600">Superpoderes</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {Math.min(totalPowerUses * 5, 100)}%
                  </div>
                  <div className="text-xs text-slate-600">Nivel de poder</div>
                </div>
              </div>
            </Card>

            <Button onClick={() => setSelectedActivity(null)} variant="secondary" className="py-3 px-6 btn-interactive">
              ← Volver
            </Button>
          </div>
        );

      case 'story':
        return (
          <div className="text-center">
            <h2 className="text-2xl sm:text-3xl font-semibold text-slate-800 mb-4">
              Crea tu Historia 📖
            </h2>
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 mb-6 border border-slate-200 shadow-sm">
              <p className="text-base sm:text-lg text-slate-700 font-medium">
                ✏️ {DIARY_PROMPTS[Math.floor(Math.random() * DIARY_PROMPTS.length)]}
              </p>
            </div>
            <textarea
              value={storyInput}
              onChange={(e) => setStoryInput(e.target.value)}
              placeholder="Escribe tu historia aquí..."
              className="w-full h-48 p-4 border-2 border-slate-200 rounded-2xl focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 mb-6 text-sm sm:text-base bg-white/80 backdrop-blur-sm shadow-sm transition-all"
            />
            <div className="flex gap-3 justify-center">
              <Button 
                onClick={() => {
                  if (storyInput.trim()) {
                    storageService.saveStory(storyInput);
                    setStoryInput('');
                    alert('¡Historia guardada!');
                  }
                }} 
                className="btn-interactive bg-gradient-to-r from-emerald-400 to-teal-400 hover:from-emerald-500 hover:to-teal-500 text-white font-semibold py-3 px-8 text-lg rounded-full shadow-md hover:shadow-lg transition-all"
              >
                💾 Guardar
              </Button>
              <Button onClick={() => setSelectedActivity(null)} variant="secondary" className="py-3 px-6 btn-interactive">
                ← Volver
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <ThemedScreen className="p-4 sm:p-6 md:p-8 pb-24">
      <div className="w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button 
            onClick={() => selectedActivity ? setSelectedActivity(null) : onNavigate('home')} 
            className="p-3 sm:p-4 bg-white/80 backdrop-blur-sm rounded-full shadow-lg hover:shadow-xl transition-all btn-interactive border border-slate-200"
          >
            <span className="text-xl sm:text-2xl">←</span>
          </button>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-semibold text-slate-800">
            {selectedActivity ? 'Actividad' : 'Jugar y Crear'} 🎨
          </h1>
          <div className="w-12 sm:w-16"></div>
        </div>

        {!selectedActivity ? (
          <>
            {/* Description */}
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 mb-6 border border-slate-200 shadow-md">
              <p className="text-base sm:text-lg text-slate-700 text-center font-medium">
                🎮 Elige una actividad divertida para jugar y crear
              </p>
            </div>

            {/* Calmi Character */}
            <div className="flex justify-center mb-6">
              <div className="animate-subtle-float">
                <CalmiCharacter size="medium" />
              </div>
            </div>

            {/* Activities Grid */}
            <div className="grid grid-cols-2 gap-4 mb-20">
              {PLAY_ACTIVITIES.map((activity) => (
                <Card
                  key={activity.id}
                  className="p-4 sm:p-6 hover:shadow-lg transition-all cursor-pointer btn-interactive bg-white/80 backdrop-blur-sm border border-slate-200 hover:border-blue-300"
                  onClick={() => setSelectedActivity(activity.id)}
                >
                  <div className="text-4xl sm:text-5xl md:text-6xl mb-3 animate-subtle-float">{activity.icon}</div>
                  <div className="text-base sm:text-lg font-semibold text-slate-800 mb-2">{activity.label}</div>
                  <div className="text-xs sm:text-sm text-slate-600 font-medium">{activity.description}</div>
                </Card>
              ))}
            </div>
          </>
        ) : (
          <div className="mb-20">
            {renderActivity()}
          </div>
        )}

        {/* Bottom Navigation */}
        <NavigationBar activeItem="play" onNavigate={onNavigate} />
      </div>
    </ThemedScreen>
  );
};

export default PlayScreen;
